import { useCallback } from 'react'

import { useAppDispatch, useAppSelector } from 'store/store'

import { User } from 'types/app'

import * as actions from './actions'
import { actions as authActions } from './slice'
import {
  SignInParams,
  SignUpParams,
  ActivationParams,
  UpdateParams,
} from './types'

export const useAuth = () => {
  const dispatch = useAppDispatch()

  const signInUser = useCallback(
    (payload: SignInParams) => {
      dispatch(actions.signInUser(payload))
    },
    [dispatch],
  )

  const signUpUser = useCallback(
    (payload: SignUpParams) => {
      dispatch(actions.signUpUser(payload))
    },
    [dispatch],
  )

  const signOut = useCallback(() => {
    dispatch(authActions.signOut())
  }, [dispatch])

  const activateUser = useCallback(
    (payload: ActivationParams) => {
      dispatch(actions.activateUser(payload))
    },
    [dispatch],
  )

  const updateUser = useCallback(
    (payload: UpdateParams) => {
      dispatch(actions.updateUser(payload))
    },
    [dispatch],
  )

  const updateUserState = useCallback(
    (payload: Partial<User>) => {
      dispatch(authActions.updateUserState(payload))
    },
    [dispatch],
  )

  const requestToBecomeOrganizer = useCallback(() => {
    dispatch(actions.requestToBecomeOrganizer()) // Provide an empty object as argument
  }, [dispatch])

  const approveOrRejectOrganizerRequest = useCallback(
    (payload: any) => {
      dispatch(actions.approveOrRejectOrganizerRequest(payload))
    },
    [dispatch],
  )

  const getAllRequests = useCallback(() => {
    dispatch(actions.getAllRequests())
  }, [dispatch])

  const followUser = useCallback(
    (userId: string) => {
      dispatch(actions.followUser(userId))
    },
    [dispatch],
  )

  const getUserWithName = useCallback(
    (username: string) => {
      dispatch(actions.getUserInfo(username))
    },
    [dispatch],
  )

  const updateMetamaskAddress = useCallback(
    (id: string, address: string) => {
      dispatch(actions.updateUserAccountAddress({ id, address }))
    },
    [dispatch],
  )

  const getUserBalance = useCallback(() => {
    dispatch(actions.getUserBalance())
  }, [dispatch])

  const refreshCurrentUser = useCallback(() => {
    dispatch(actions.getCurrentUser())
  }, [dispatch])

  const messageCleanUp = useCallback(() => {
    dispatch(authActions.messageCleanUp())
  }, [dispatch])

  const authState = useAppSelector(({ auth }) => auth)
  const message = authState.message

  const isAdmin = authState.user?.user_type === 'admin'
  const isOrganizer = authState.user?.user_type === 'organizer'

  return {
    ...authState,
    isAdmin,
    isOrganizer,
    message,
    signOut,
    signInUser,
    signUpUser,
    activateUser,
    updateUser,
    updateUserState,
    requestToBecomeOrganizer,
    getAllRequests,
    approveOrRejectOrganizerRequest,
    followUser,
    getUserWithName,
    updateMetamaskAddress,
    getUserBalance,
    refreshCurrentUser,
    messageCleanUp,
  }
}
